import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import {
  Briefcase,
  CheckCircle2,
  Clock,
  Building2,
  GraduationCap,
  PoundSterling,
  Stethoscope,
  ArrowRight,
  ChevronRight,
  AlertTriangle,
  Users,
  FileText,
  Target,
} from 'lucide-react';

const TrustGradeGuidePage = () => {
  // Get current date for Article schema
  const publishDate = '2025-01-15';
  const modifiedDate = new Date().toISOString().split('T')[0];

  const whatIsTrustGrade = [
    {
      icon: Building2,
      title: 'Direct NHS Employment',
      description: 'Trust Grade doctors are employed directly by NHS Trusts rather than through training programmes. This gives you stability and NHS experience.',
    },
    {
      icon: Stethoscope,
      title: 'Clinical Experience',
      description: 'Work at SHO or Registrar level across various specialties. You\'ll gain hands-on clinical experience that counts towards your NHS portfolio.',
    },
    {
      icon: GraduationCap,
      title: 'Not a Training Post',
      description: 'Unlike Foundation or Specialty Training posts, Trust Grade roles are not part of formal training programmes — meaning they\'re fully open to IMGs.',
    },
    {
      icon: Clock,
      title: 'Flexible Duration',
      description: 'Contracts typically range from 6 months to 2+ years, with many Trusts offering extensions or permanent positions.',
    },
  ];

  const benefits = [
    {
      title: 'Not Affected by Prioritisation Act',
      description: 'The Medical Training Prioritisation Act 2026 does not apply to Trust Grade positions. IMGs compete on equal footing with UK graduates.',
      highlight: true,
      link: '/img-guide/medical-training-prioritisation-act-2026',
      linkText: 'Learn about the Act →',
    },
    {
      title: 'Build NHS Experience',
      description: 'Time spent in Trust Grade roles counts towards your NHS experience, which may qualify you for secondary priority in future training applications.',
      highlight: false,
    },
    {
      title: 'Visa Sponsorship Available',
      description: 'Many NHS Trusts sponsor Skilled Worker visas for Trust Grade positions. This is often easier to obtain than for training posts.',
      highlight: false,
    },
    {
      title: 'Competitive Salary',
      description: 'Trust Grade doctors are paid on NHS pay scales, typically £43,923 - £63,152 depending on experience and seniority level.',
      highlight: false,
    },
    {
      title: 'Portfolio Development',
      description: 'Opportunities for audits, QI projects, teaching, and leadership — all valuable for future training applications.',
      highlight: false,
    },
    {
      title: 'Specialty Exposure',
      description: 'Choose roles in your preferred specialty to build relevant experience for competitive training applications.',
      highlight: false,
    },
  ];

  const howToApply = [
    {
      step: '1',
      title: 'Ensure GMC Registration',
      description: 'You need full GMC registration with a licence to practise. Most IMGs obtain this via PLAB or through qualification recognition.',
    },
    {
      step: '2',
      title: 'Set Up Job Alerts',
      description: 'Trust Grade positions fill quickly — often within 48 hours. Use TRACJobsAlert to get instant notifications when new roles are posted.',
    },
    {
      step: '3',
      title: 'Prepare Your Application',
      description: 'Have your CV, GMC certificate, right to work evidence, and references ready. Tailor your personal statement for each specialty.',
    },
    {
      step: '4',
      title: 'Apply Immediately',
      description: 'When you receive an alert for a suitable role, apply the same day. Early applications have significantly higher success rates.',
    },
  ];

  const faqs = [
    {
      question: 'What is the difference between Trust Grade and Specialty Doctor?',
      answer: 'Trust Grade is typically at SHO or junior registrar level, while Specialty Doctor is a more senior grade requiring substantial post-qualification experience (usually 4+ years). Both are non-training posts open to IMGs.',
    },
    {
      question: 'Can Trust Grade experience help me get into specialty training?',
      answer: 'Yes. NHS experience in Trust Grade roles counts towards your portfolio and may qualify you for secondary priority consideration under the Medical Training Prioritisation Act. It also demonstrates clinical competence to selection panels.',
    },
    {
      question: 'How quickly do Trust Grade jobs get filled?',
      answer: 'Very quickly. Popular Trust Grade posts, especially in competitive specialties or desirable locations, can receive dozens of applications within 24-48 hours of posting. Instant job alerts are essential.',
    },
    {
      question: 'Do Trust Grade doctors get the same training opportunities?',
      answer: 'While not part of a formal training programme, many Trusts provide excellent training opportunities including study leave, course funding, and supervision. However, this varies by Trust and department.',
    },
    {
      question: 'Can I apply for Trust Grade jobs from outside the UK?',
      answer: 'Yes, if you have GMC registration (or are in the process of obtaining it) and right to work in the UK. Many Trusts interview candidates remotely and sponsor Skilled Worker visas.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Trust Grade Doctor Jobs UK | Alerts for IMGs — TRACJobsAlert</title>
        <meta name="description" content="Get instant alerts for NHS Trust Grade doctor vacancies across the UK. Built for international medical graduates building NHS experience. Not affected by the 2026 Prioritisation Act." />
        <meta name="keywords" content="Trust Grade doctor jobs UK, Trust Grade NHS jobs, IMG Trust Grade positions, NHS Trust doctor jobs, Trust Grade vs training post, Trust Grade doctor salary, Trust Grade jobs for IMGs" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/img-guide/trust-grade-jobs" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://tracjobsalert.co.uk/img-guide/trust-grade-jobs" />
        <meta property="og:title" content="Trust Grade Doctor Jobs UK | Alerts for IMGs" />
        <meta property="og:description" content="Get instant alerts for NHS Trust Grade doctor vacancies across the UK. Built for international medical graduates." />
        <meta property="og:image" content="https://tracjobsalert.co.uk/icon-512.png" />
        <meta property="og:site_name" content="TRACJobsAlert" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Trust Grade Doctor Jobs UK | TRACJobsAlert" />
        <meta name="twitter:description" content="Complete guide to Trust Grade positions for IMGs. Not affected by the 2026 Prioritisation Act." />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Trust Grade Doctor Jobs UK: Complete Guide for IMGs",
            "description": "Everything international medical graduates need to know about Trust Grade NHS positions - what they are, pay scales, how to apply, and why they're your best route to NHS experience.",
            "author": {
              "@type": "Organization",
              "name": "TRACJobsAlert"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TRACJobsAlert",
              "logo": {
                "@type": "ImageObject",
                "url": "https://tracjobsalert.co.uk/icon-512.png"
              }
            },
            "datePublished": publishDate,
            "dateModified": modifiedDate,
            "mainEntityOfPage": "https://tracjobsalert.co.uk/img-guide/trust-grade-jobs"
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-slate-900">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-600 block -mt-1">For Doctors</span>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    Get Alerts
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-500 hover:text-emerald-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <Link to="/img-guide" className="text-slate-500 hover:text-emerald-600">IMG Guide</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-900 font-medium">Trust Grade Jobs</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-b from-slate-50 to-white py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium mb-6">
              <Briefcase className="w-4 h-4" />
              NHS Non-Training Position
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Trust Grade Doctor Jobs UK
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              Trust Grade positions are your gateway to NHS experience. Fully open to international medical graduates, 
              not affected by the <Link to="/img-guide/medical-training-prioritisation-act-2026" className="text-emerald-600 hover:text-emerald-700 underline">2026 Prioritisation Act</Link>, and offering competitive salaries with visa sponsorship.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg">
                  Get Trust Grade Job Alerts
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What is Trust Grade */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              What is a Trust Grade Doctor?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Trust Grade doctors are employed directly by NHS Trusts to provide clinical services outside of formal training programmes.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whatIsTrustGrade.map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits for IMGs */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Why Trust Grade Jobs are Ideal for IMGs
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Trust Grade positions offer unique advantages for international medical graduates building their NHS careers.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`rounded-xl p-6 border ${benefit.highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className={`w-6 h-6 mt-0.5 flex-shrink-0 ${benefit.highlight ? 'text-emerald-600' : 'text-emerald-500'}`} />
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 mb-2">{benefit.title}</h3>
                      <p className="text-slate-600 text-sm">{benefit.description}</p>
                      {benefit.link && (
                        <Link to={benefit.link} className="text-emerald-600 text-sm font-medium mt-2 inline-flex items-center gap-1 hover:text-emerald-700">
                          {benefit.linkText}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Apply */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              How to Get a Trust Grade Job
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto text-center mb-12">
              Follow these steps to maximise your chances of securing a Trust Grade position.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howToApply.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 border border-slate-200 h-full">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4">
                      {item.step}
                    </div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-emerald-500">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Don't Miss Your Next Trust Grade Opportunity
            </h2>
            <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
              Trust Grade positions fill within 24-48 hours. Get instant email alerts when new jobs matching your specialty and location are posted.
            </p>
            <Link to="/register">
              <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-semibold">
                Start Getting Alerts — £9.99/month
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-emerald-200 text-sm mt-4">Cancel anytime. No contract.</p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 text-center">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
              Common questions about Trust Grade doctor positions in the UK.
            </p>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Guides</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/img-guide/led-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">LED Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Complete guide to Locally Employed Doctor positions.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/medical-training-prioritisation-act-2026" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Medical Training Prioritisation Act</h3>
                <p className="text-slate-600 text-sm mb-4">What the 2026 Act means for your career.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
              <Link to="/img-guide/plab-jobs" className="bg-white rounded-xl p-6 border border-slate-200 hover:border-emerald-500 transition-colors">
                <h3 className="font-semibold text-lg text-slate-900 mb-2">PLAB Doctor Jobs</h3>
                <p className="text-slate-600 text-sm mb-4">Finding NHS jobs after passing PLAB.</p>
                <span className="text-emerald-600 text-sm font-medium flex items-center gap-1">
                  Read guide <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="font-bold text-white">Trac Jobs Alert</span>
                  <span className="text-xs text-emerald-400 block">For Doctors</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link>
                <Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link>
                <Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link>
                <Link to="/img-guide" className="text-slate-400 hover:text-white">IMG Guide</Link>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
              © 2025 TRACJobsAlert. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TrustGradeGuidePage;
